import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import {
  LinkOff as DisconnectIcon,
  Edit as EditIcon,
  Refresh as RetryIcon,
} from '@mui/icons-material';
import { relationshipOverridesApi } from '../../services/api';

function ProductCard({ product }) {
  if (!product) return null;
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {product.image_url && (
        <Box
          component="img"
          src={product.image_url}
          alt=""
          sx={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 1, mb: 1 }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      )}
      <Typography variant="caption" color="text.secondary" display="block">
        {product.retailer_id}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {product.name}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {[product.brand, product.color].filter(Boolean).join(' · ')}
      </Typography>
      {product.product_url && (
        <Typography
          variant="caption"
          component="a"
          href={product.product_url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'primary.main', textDecoration: 'none' }}
        >
          View product ↗
        </Typography>
      )}
    </Box>
  );
}

export default function DisconnectModal({ open, productUuidA, productUuidB, matchType, onClose, onSuccess }) {
  const [phase, setPhase] = useState('loading'); // loading | error | review | saving | done
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [reason, setReason] = useState('');
  const [reasonEditing, setReasonEditing] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [adminNote, setAdminNote] = useState('');
  const [allowedTags, setAllowedTags] = useState([]);

  const loadPreview = useCallback(async () => {
    if (!productUuidA || !productUuidB) return;
    setPhase('loading');
    setError(null);
    try {
      // Load signal tags and AI preview in parallel — tags available even if AI fails
      const [data, tagsData] = await Promise.allSettled([
        relationshipOverridesApi.preview(productUuidA, productUuidB),
        relationshipOverridesApi.getSignalTags(),
      ]);

      if (tagsData.status === 'fulfilled') {
        setAllowedTags(tagsData.value.tags || []);
      }

      if (data.status === 'rejected') {
        throw data.reason;
      }

      setPreview(data.value);
      setReason(data.value.ai_reason || '');
      setSelectedTags(data.value.signal_tags || []);
      if (!tagsData.value) setAllowedTags(data.value.allowed_tags || []);
      setPhase('review');
    } catch (err) {
      setError(err.message || 'AI generation failed');
      setPhase('error');
    }
  }, [productUuidA, productUuidB]);

  useEffect(() => {
    if (open) {
      setPhase('loading');
      setPreview(null);
      setReason('');
      setReasonEditing(false);
      setSelectedTags([]);
      setAdminNote('');
      loadPreview();
    }
  }, [open, loadPreview]);

  const handleConfirm = async () => {
    setPhase('saving');
    try {
      // Persist any new tags that aren't in the allowlist
      const newTags = selectedTags.filter(t => !allowedTags.includes(t));
      for (const tag of newTags) {
        await relationshipOverridesApi.createSignalTag(tag);
      }

      await relationshipOverridesApi.create({
        product_uuid_a: productUuidA,
        product_uuid_b: productUuidB,
        override_type: 'excluded',
        ai_reason: reason,
        signal_tags: selectedTags,
        admin_note: adminNote || undefined,
      });
      setPhase('done');
    } catch (err) {
      setError(err.message || 'Failed to save override');
      setPhase('error');
    }
  };

  const handleClose = () => {
    if (phase === 'done') onSuccess?.();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DisconnectIcon color="error" />
        Disconnect product match
      </DialogTitle>

      <DialogContent dividers>
        {phase === 'loading' && (
          <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={2}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              Analysing products…
            </Typography>
          </Box>
        )}

        {phase === 'error' && (
          <Box display="flex" flexDirection="column" gap={2}>
            <Alert
              severity="warning"
              action={
                <Button size="small" startIcon={<RetryIcon />} onClick={loadPreview}>
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
            <Alert severity="info">
              You can still disconnect manually — fill in the reason and tags below.
            </Alert>
            <TextField
              label="Reason"
              multiline
              minRows={2}
              fullWidth
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why these products are not the same…"
            />
            <Autocomplete
              multiple
              freeSolo
              options={allowedTags}
              value={selectedTags}
              onChange={(_, val) => setSelectedTags(val)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip label={option} size="small" {...getTagProps({ index })} key={option} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Signal tags" placeholder="Add tag…" size="small" />
              )}
            />
          </Box>
        )}

        {(phase === 'review' || phase === 'saving') && preview && (
          <Box display="flex" flexDirection="column" gap={2}>
            {matchType === 'hub_transitive' && (
              <Alert severity="warning">
                This is a <strong>transitive match</strong> — it was inferred via a shared hub product, not matched directly. Disconnecting it here may not prevent it from being rebuilt on the next ETL run. To permanently remove it, find and disconnect the <strong>root embedding match</strong> that forms the hub instead.
              </Alert>
            )}
            {/* Side-by-side product cards */}
            <Box display="flex" gap={2}>
              <ProductCard product={preview.product_a} />
              <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main', px: 1 }}>
                <DisconnectIcon />
              </Box>
              <ProductCard product={preview.product_b} />
            </Box>

            {/* AI reason */}
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                <Typography variant="subtitle2">Reason</Typography>
                {!reasonEditing && (
                  <Tooltip title="Edit reason">
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => setReasonEditing(true)}
                      sx={{ textTransform: 'none' }}
                    >
                      Edit
                    </Button>
                  </Tooltip>
                )}
              </Box>
              {reasonEditing ? (
                <TextField
                  multiline
                  minRows={2}
                  fullWidth
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  size="small"
                  autoFocus
                />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {reason}
                </Typography>
              )}
            </Box>

            {/* Signal tags */}
            <Box>
              <Typography variant="subtitle2" mb={0.5}>Signal tags</Typography>
              <Autocomplete
                multiple
                freeSolo
                options={allowedTags}
                value={selectedTags}
                onChange={(_, val) => setSelectedTags(val)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} size="small" color="primary" variant="outlined" {...getTagProps({ index })} key={option} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Add tag…" size="small" />
                )}
              />
            </Box>

            {/* Optional admin note */}
            <TextField
              label="Admin note (optional)"
              size="small"
              fullWidth
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              placeholder="Any additional context…"
            />
          </Box>
        )}

        {phase === 'done' && preview && (
          <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={2}>
            <DisconnectIcon color="error" sx={{ fontSize: 48 }} />
            <Typography variant="body1" fontWeight={500}>Match disconnected.</Typography>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2">
                <strong>{preview.product_a?.name}</strong>
                <Typography component="span" variant="caption" color="text.secondary"> ({preview.product_a?.retailer_id})</Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary">disconnected from</Typography>
              <Typography variant="body2">
                <strong>{preview.product_b?.name}</strong>
                <Typography component="span" variant="caption" color="text.secondary"> ({preview.product_b?.retailer_id})</Typography>
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Saved to overrides — will not reappear after the next ETL run.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {phase === 'done' ? (
          <Button onClick={handleClose} variant="contained">Done</Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={phase === 'saving'}>Cancel</Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
              color="error"
              disabled={phase === 'loading' || phase === 'saving' || !reason.trim()}
              startIcon={phase === 'saving' ? <CircularProgress size={16} color="inherit" /> : <DisconnectIcon />}
            >
              {phase === 'saving' ? 'Disconnecting…' : 'Confirm disconnect'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
